import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { FileText, Download, Loader2, FileCheck, Clock, Sparkles, FileUp, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';
import UpgradePrompt from '../components/UpgradePrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { id: 'resume', name: 'Resume', icon: FileText, description: 'Professional resume showcasing your experience' },
  { id: 'cover_letter', name: 'Cover Letter', icon: FileText, description: 'Compelling cover letter for job applications' },
  { id: 'reference_letter', name: 'Reference Letter', icon: FileText, description: 'Template for supervisors/colleagues to complete' },
  { id: 'business_plan', name: 'Business Plan', icon: FileText, description: 'Comprehensive plan for veteran entrepreneurs' },
];

const DocumentGenerator = () => {
  const { token } = useAuth();
  const { hasFullAccess, isPreviewMode } = useSubscription();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [selectedType, setSelectedType] = useState('resume');
  const [templateStyle, setTemplateStyle] = useState('military_transition');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [currentDocId, setCurrentDocId] = useState(null);

  useEffect(() => {
    if (hasFullAccess) {
      fetchDocuments();
    } else {
      setLoadingDocs(false);
    }
  }, [token, hasFullAccess]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const generateDocument = async () => {
    setLoading(true);
    setGeneratedContent('');
    try {
      const response = await axios.post(`${API_URL}/api/documents/generate`, {
        doc_type: selectedType,
        template_style: templateStyle,
        job_title: jobTitle,
        company_name: companyName,
        additional_info: additionalInfo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedContent(response.data.content);
      setCurrentDocId(response.data.doc_id);
      toast.success('Document generated successfully!');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate document');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (docId, format) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}/download?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document exported! Upload to Google Drive to open in Google Docs.');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const loadDocument = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedContent(response.data.content);
      setCurrentDocId(docId);
      setSelectedType(response.data.doc_type);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="document-generator-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">Document Generator</h1>
          <p className="text-navy-600 dark:text-navy-300">Create professional documents tailored for your transition</p>
        </div>

        {/* Preview Mode - Show Upgrade */}
        {isPreviewMode && (
          <div className="mb-8">
            <UpgradePrompt 
              feature="the Document Generator"
              title="Unlock Document Generation"
              description="Create professional resumes, cover letters, reference letters, and business plans with AI-powered document generation."
            />
          </div>
        )}

        {/* Show feature preview for non-subscribers */}
        {isPreviewMode && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <FileText className="w-5 h-5 text-amber-600" />
                  Available Document Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {DOCUMENT_TYPES.map((type) => (
                    <div key={type.id} className="flex items-center gap-3 p-3 bg-navy-50 dark:bg-navy-700/50 rounded-lg">
                      <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center">
                        <type.icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-navy-900 dark:text-white">{type.name}</p>
                        <p className="text-sm text-navy-500 dark:text-navy-400">{type.description}</p>
                      </div>
                      <Lock className="w-5 h-5 text-navy-400 ml-auto" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  What You Get
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-navy-600 dark:text-navy-300">
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>AI-powered document generation based on your profile</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Military-to-civilian language translation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Multiple template styles for different industries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Export to Google Docs compatible format</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Save and edit documents anytime</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full Access Content */}
        {hasFullAccess && (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left Panel - Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  Generate New Document
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-navy-700 dark:text-navy-300">Document Type</Label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="mt-1 dark:bg-navy-700 dark:border-navy-600" data-testid="doc-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-navy-500 mt-1">
                    {DOCUMENT_TYPES.find(t => t.id === selectedType)?.description}
                  </p>
                </div>

                <div>
                  <Label className="text-navy-700">Template Style</Label>
                  <Select value={templateStyle} onValueChange={setTemplateStyle}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="military_transition">Military-to-Civilian Focused</SelectItem>
                      <SelectItem value="standard">Standard Professional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(selectedType === 'resume' || selectedType === 'cover_letter') && (
                  <>
                    <div>
                      <Label className="text-navy-700">Target Position</Label>
                      <Input
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        placeholder="e.g., Project Manager"
                        className="mt-1"
                        data-testid="doc-job-title-input"
                      />
                    </div>
                    <div>
                      <Label className="text-navy-700">Target Company</Label>
                      <Input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g., Tech Solutions Inc."
                        className="mt-1"
                      />
                    </div>
                  </>
                )}

                {selectedType === 'business_plan' && (
                  <div>
                    <Label className="text-navy-700">Business Idea Description</Label>
                    <Textarea
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      placeholder="Describe your business concept..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>
                )}

                <Button 
                  onClick={generateDocument} 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                  data-testid="generate-doc-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Document
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Previous Documents */}
            <Card className="border-navy-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-navy-900">
                  <Clock className="w-4 h-4 text-navy-500" />
                  Previous Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingDocs ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-navy-400" />
                  </div>
                ) : documents.length > 0 ? (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div 
                          key={doc.doc_id}
                          onClick={() => loadDocument(doc.doc_id)}
                          className={`
                            flex items-center justify-between p-3 rounded-lg cursor-pointer border
                            ${currentDocId === doc.doc_id ? 'border-amber-300 bg-amber-50' : 'border-navy-100 hover:bg-navy-50'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <FileCheck className="w-4 h-4 text-amber-600" />
                            <div>
                              <p className="text-sm font-medium text-navy-900 capitalize">
                                {doc.doc_type.replace('_', ' ')}
                              </p>
                              <p className="text-xs text-navy-500">
                                {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-center text-navy-400 text-sm py-4">No documents yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Preview */}
          <div className="lg:col-span-3">
            <Card className="border-navy-100 h-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-navy-900">
                  <FileText className="w-5 h-5 text-amber-600" />
                  Document Preview
                </CardTitle>
                {currentDocId && (
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="default" 
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => downloadDocument(currentDocId, 'docx')}
                      data-testid="download-gdocs-btn"
                    >
                      <FileUp className="w-4 h-4 mr-1.5" />
                      Export for Google Docs
                    </Button>
                    <span className="text-xs text-navy-400 hidden sm:inline">
                      (.docx - Upload to Google Drive)
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] rounded-lg border border-navy-100 bg-white p-6">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-amber-600 mb-4" />
                      <p className="text-navy-600 dark:text-navy-300">Generating your document...</p>
                      <p className="text-sm text-navy-400 mt-1">This may take a moment</p>
                    </div>
                  ) : generatedContent ? (
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-navy-800 dark:text-navy-200 leading-relaxed">
                        {generatedContent}
                      </pre>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <FileText className="w-16 h-16 text-navy-200 dark:text-navy-600 mb-4" />
                      <h3 className="font-heading font-semibold text-navy-700 dark:text-navy-300 mb-2">
                        No document yet
                      </h3>
                      <p className="text-navy-500 dark:text-navy-400 max-w-sm">
                        Select a document type and click Generate to create your professional document
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DocumentGenerator;
